import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Popover,
  Chip,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  subDays,
  startOfYear,
  endOfYear,
  subYears,
  startOfDay,
  endOfDay,
  format,
} from "date-fns";

const getShortcuts = () => [
  { label: "Today", start: startOfDay(new Date()), end: endOfDay(new Date()) },
  { label: "Yesterday", start: startOfDay(subDays(new Date(), 1)), end: endOfDay(subDays(new Date(), 1)) },
  { label: "Last 7 Days", start: subDays(new Date(), 7), end: new Date() },
  { label: "Last 30 Days", start: subDays(new Date(), 30), end: new Date() },
  { label: "This Month", start: startOfMonth(new Date()), end: endOfMonth(new Date()) },
  { label: "Last Month", start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) },
  { label: "Last 2 Months", start: startOfMonth(subMonths(new Date(), 2)), end: endOfMonth(subMonths(new Date(), 1)) },
  { label: "Last 3 Months", start: startOfMonth(subMonths(new Date(), 3)), end: endOfMonth(subMonths(new Date(), 1)) },
  { label: "Last 6 Months", start: startOfMonth(subMonths(new Date(), 6)), end: endOfMonth(subMonths(new Date(), 1)) },
  { label: "This Quarter", start: startOfQuarter(new Date()), end: endOfQuarter(new Date()) },
  { label: "Last Quarter", start: startOfQuarter(subQuarters(new Date(), 1)), end: endOfQuarter(subQuarters(new Date(), 1)) },
  { label: "This Year", start: startOfYear(new Date()), end: endOfYear(new Date()) },
  { label: "Last Year", start: startOfYear(subYears(new Date(), 1)), end: endOfYear(subYears(new Date(), 1)) },
];

const formatDisplay = (startDate, endDate) => {
  if (!startDate && !endDate) return "";
  const s = startDate ? format(new Date(startDate), "d MMM, yy") : "—";
  const e = endDate ? format(new Date(endDate), "d MMM, yy") : "—";
  return `${s}  –  ${e}`;
};

const DateRangePicker = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const open = Boolean(anchorEl);

  const startDate = value?.startDate || null;
  const endDate = value?.endDate || null;

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStartChange = (date) => {
    onChange({ startDate: date, endDate });
  };

  const handleEndChange = (date) => {
    onChange({ startDate, endDate: date });
  };

  const handleShortcut = (shortcut) => {
    onChange({ startDate: shortcut.start, endDate: shortcut.end });
    handleClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Display field */}
      <TextField
        ref={inputRef}
        fullWidth
        size="medium"
        label="Date Range"
        value={formatDisplay(startDate, endDate)}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleOpen}>
                <CalendarMonthIcon sx={{ fontSize: "1.4rem", color: "#666" }} />
              </IconButton>
            </InputAdornment>
          ),
          sx: { cursor: "pointer" },
        }}
        sx={{
          "& .MuiInputBase-input": {
            cursor: "pointer",
            fontSize: "1rem",
          },
        }}
      />

      {/* Popover with shortcuts + pickers */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", minWidth: 540 }}>
          {/* Shortcuts panel */}
          <Box
            sx={{
              width: 160,
              borderRight: "1px solid #eee",
              py: 1.5,
              px: 1,
              maxHeight: 380,
              overflowY: "auto",
              bgcolor: "#fafafa",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                px: 1,
                pb: 0.5,
                display: "block",
                fontWeight: 700,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.6rem",
              }}
            >
              Quick Select
            </Typography>
            {getShortcuts().map((s) => (
              <Chip
                key={s.label}
                label={s.label}
                size="small"
                onClick={() => handleShortcut(s)}
                sx={{
                  m: 0.3,
                  fontSize: "0.72rem",
                  height: 26,
                  cursor: "pointer",
                  bgcolor: "#fff",
                  border: "1px solid #e0e0e0",
                  fontWeight: 500,
                  "&:hover": {
                    bgcolor: "rgba(94, 79, 170, 0.08)",
                    borderColor: "rgba(94, 79, 170, 0.5)",
                  },
                }}
              />
            ))}
          </Box>

          {/* Date pickers */}
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.6rem",
              }}
            >
              Custom Range
            </Typography>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={handleStartChange}
              maxDate={endDate || undefined}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={handleEndChange}
              minDate={startDate || undefined}
              maxDate={new Date()}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
